import { Fragment, useState } from 'react';

import { Checkbox, Table, Tbody, Td, Text, Th, Thead, Tr, VStack } from '@bonusly/design-system';

import { NotificationChannel, NotificationSettings } from './types';
import {
	getSetting,
	isCategoryChannelChecked,
	isCategoryChannelIndeterminate,
	toggleCategoryChannel,
	toggleSetting,
} from './utils/notification-settings-form-toggling';
import { NOTIFICATION_CONFIG } from './utils/notification-settings-mapper';

const EMAIL_CHANNEL: NotificationChannel = 'email';
const SLACK_CHANNEL: NotificationChannel = 'slack';
const MICROSOFT_TEAMS_CHANNEL: NotificationChannel = 'microsoftTeams';

interface NotificationsSettingsFormProps {
	initialSettings?: NotificationSettings;
	onChange?: (settings: NotificationSettings) => void;
}

// Channels to display in the table (order matters for UI)
const AVAILABLE_CHANNELS: NotificationChannel[] = [EMAIL_CHANNEL, SLACK_CHANNEL, MICROSOFT_TEAMS_CHANNEL];

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
	[EMAIL_CHANNEL]: 'Email',
	[SLACK_CHANNEL]: 'Slack',
	[MICROSOFT_TEAMS_CHANNEL]: 'Microsoft Teams',
};

export const NotificationsSettingsForm = ({ initialSettings = [], onChange }: NotificationsSettingsFormProps) => {
	const [settings, setSettings] = useState<NotificationSettings>(initialSettings);

	// Toggle individual setting
	const handleToggleSetting = (topicId: string, channel: NotificationChannel) => {
		const newSettings = toggleSetting(settings, topicId, channel);
		setSettings(newSettings);
		onChange?.(newSettings);
	};

	// Toggle all settings in a category for a specific channel
	const handleToggleCategory = (categoryId: string, channel: NotificationChannel) => {
		const newSettings = toggleCategoryChannel(settings, categoryId, channel);
		setSettings(newSettings);
		onChange?.(newSettings);
	};

	return (
		<VStack spacing={0} align="stretch">
			<Table variant="simple" width="100%">
				<Thead>
					<Tr>
						<Th pl={4} borderBottom="1px solid" borderColor="gray.200">
							<Text fontSize="sm" fontWeight="medium" color="gray.600">
								Notification
							</Text>
						</Th>

						{AVAILABLE_CHANNELS.map((channel) => (
							<Th key={channel} textAlign="center" borderBottom="1px solid" borderColor="gray.200">
								<Text fontSize="sm" fontWeight="medium" color="gray.600">
									{CHANNEL_LABELS[channel]}
								</Text>
							</Th>
						))}
					</Tr>
				</Thead>
				<Tbody>
					{NOTIFICATION_CONFIG.categories.map((category) => (
						<Fragment key={category.id}>
							<Tr>
								<Td pl={4} py={3}>
									<Text fontWeight="semibold" fontSize="sm" color="gray.800">
										{category.name}
									</Text>
								</Td>
								{AVAILABLE_CHANNELS.map((channel) => (
									<Td key={channel} textAlign="center" py={3}>
										<Checkbox
											isChecked={isCategoryChannelChecked(settings, category.id, channel)}
											isIndeterminate={isCategoryChannelIndeterminate(settings, category.id, channel)}
											onChange={() => handleToggleCategory(category.id, channel)}
											colorScheme="blue"
											size="md"
										/>
									</Td>
								))}
							</Tr>
							{category.topics.map((topic) => (
								<Tr key={topic.id} bg="gray.25">
									<Td pl={4} py={3} verticalAlign="top">
										<VStack align="start" spacing={1}>
											<Text fontWeight="medium" fontSize="sm" color="gray.800">
												{topic.name}
											</Text>
											<Text fontSize="xs" color="gray.600" lineHeight="short">
												{topic.description}
											</Text>
										</VStack>
									</Td>
									{AVAILABLE_CHANNELS.map((channel) => (
										<Td key={channel} textAlign="center" py={3}>
											{topic.availableChannels.includes(channel) && (
												<Checkbox
													isChecked={getSetting(settings, topic.id, channel)}
													onChange={() => handleToggleSetting(topic.id, channel)}
													colorScheme="blue"
													size="md"
												/>
											)}
										</Td>
									))}
								</Tr>
							))}
						</Fragment>
					))}
				</Tbody>
			</Table>
		</VStack>
	);
};
